import React, { useContext, useState } from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Paper,
  Button,
  Typography,
  CircularProgress,
  Grid,
  Box,
} from '@mui/material';
import { useGetCompaniesList } from '@features/applications/hooks/useGetCompaniesList';
import { Helmet } from 'react-helmet';
import { Stack } from '@mui/system';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import ManageCompanyData from '@features/applications/components/ManageCompanyData/ManageCompanyData';
import AddCompanyEnterpriseModal from '@features/applications/components/AddCompanyEnterpriseModal/AddCompanyEnterpriseModal';
import { darkBlue, white } from '@styles/colors';
import { medium } from '@styles/fontWeights';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import { authContext } from '@staffedupusa/shared-components';
import NeedCustomHiringGraphicModal from '@features/boostMyJobs/components/CustomHiringGraphicModal/NeedCustomHiringGraphicModal';
import AdminPostHiringLinkModal from '@features/superAdmin/components/AdminPostHiringLinkModal';
import useDebounce from '@hooks/UseDebounce';
import { Employer } from '@features/superAdmin';

type Company = {
  id: Number;
  s_name: String;
  s_state: String;
  s_city: String;
  s_slug: String;
  plan: Number;
};

function EnterPriseCompanies() {
  const { authData } = useContext(authContext);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [applicationsListAlertText, setApplicationsListAlertText] =
    useState<string>('');
  const [columnNo, setColumnNo] = useState(0);
  const [sortedDirection, setSortedDirection] = useState('asc');
  const [showAddComapaniesModal, setShowAddComapaniesModal] = useState(false);
  const [showPostJobModal, setIsPostLinkModalOpen] = useState(false);
  const [isGraphicModalOpen, setIsGraphicModalOpen] = useState(false);
  const [isSelectImageModalOpen, setIsSelectImageModalOpen] = useState(false);
  const [isGraphicRequestedModalOpen, setIsGraphicRequestedModalOpen] = useState(false);
  const debounceValue = useDebounce(searchValue, 500);

  const { searchCompaniesData, isSearchingCompaniesData } = useGetCompaniesList(
    {
      searchValue: debounceValue,
      columnNo,
      sortedDirection,
      page,
      page_length: rowsPerPage,
      setAlertText: setApplicationsListAlertText,
    },
  );
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearch = () => {
    setPage(1);
  };

  const handleSort = (column: number) => {
    const isAsc = columnNo === column && sortedDirection === 'asc';
    setSortedDirection(isAsc ? 'desc' : 'asc');
    setColumnNo(column);
  };

  const showPostHiringLinkModal = () => {
    // const hasCompanySocialImage = authData.user?.has_social_image_enterprise;

    // if (hasCompanySocialImage) {
    setIsPostLinkModalOpen(true);
    // } else {
    //   setIsGraphicModalOpen(true);
    // }
  };

  const renderSortArrow = (column: number) => {
    if (columnNo === column) {
      return sortedDirection === 'asc' ? (
        <Stack direction='row' >
          <NorthIcon fontSize='small' color="primary" sx={{ margin: "0 -1px", width: "1rem" }} />
          <SouthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
        </Stack>
      ) : (
        <Stack direction='row'>
          <NorthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
          <SouthIcon fontSize='small' color="primary" sx={{ margin: "0 -1px", width: "1rem" }} />
        </Stack>
      );
    }
    return (
      <Stack direction='row'>
        <NorthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
        <SouthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
      </Stack>
    );
  };


  return (
    <div style={{ marginBottom: '40px' }}>
      <Helmet>
        <title>Companies | StaffedUp</title>
      </Helmet>
      <Stack component="section" spacing={5} pt={5} pb={4} px={3} pl={6}>
        <Stack
          p={3}
          borderRadius="8px"
          bgcolor={white}
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
        >
          <Typography variant="h3" fontSize="28px" fontWeight="bold">
            Manage Companies
          </Typography>
        </Stack>
        <Stack
          spacing={4}
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent={{ sm: 'space-between' }}
          alignItems="center"
          sx={{ borderRadius: 5 }}
        >
          <Paper
            component="form"
            sx={{
              pl: '10px',
              display: 'flex',
              alignItems: 'center',
              maxWidth: 400,
              width: '100%',
              borderRadius: 5,
            }}
            onSubmit={(e) => e.preventDefault()}
          >
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search"
              inputProps={{ 'aria-label': 'search' }}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            <IconButton
              type="button"
              sx={{ px: '10px' }}
              aria-label="search"
              // onClick={handleSearch}
            >
              <SearchIcon />
            </IconButton>
          </Paper>
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
            <Button
              variant="contained"
              size="small"
              sx={{
                bgcolor: darkBlue,
                width: { xs: '100%', lg: '270px' },
                padding: { xs: '10px' },
              }}
              onClick={showPostHiringLinkModal}
            >
              <Typography
                fontSize="16px"
                component="span"
                lineHeight="1.1"
                sx={{ textAlign: 'center' }}
              >
                Post Job to Social:
                <FacebookIcon
                  style={{
                    fontSize: '19px',
                    verticalAlign: 'text-top',
                    marginLeft: '10px',
                  }}
                />
                <TwitterIcon
                  style={{
                    fontSize: '19px',
                    verticalAlign: 'text-top',
                    marginLeft: '7px',
                  }}
                />
                <LinkedInIcon
                  style={{
                    fontSize: '19px',
                    verticalAlign: 'text-top',
                    marginLeft: '7px',
                  }}
                />
              </Typography>
            </Button>
            <Button
              onClick={() => {
                setShowAddComapaniesModal(true);
              }}
              variant="contained"
              size="small"
              color="green"
              sx={{
                width: { xs: '100%', sm: '200px' },
                padding: { xs: '10px' },
              }}
            >
              <Typography
                fontSize="16px"
                component="span"
                lineHeight="1.1"
                sx={{ textAlign: 'center' }}
              >
                <PersonAddAlt1Icon
                  style={{
                    fontSize: '20px',
                    verticalAlign: 'text-top',
                    marginRight: '7px',
                  }}
                />
                Invite Companies
              </Typography>
            </Button>
          </Stack>
        </Stack>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <b></b>
                  {/* <Stack direction='row' justifyContent='space-between'>
                        {renderSortArrow(0)}
                      </Stack> */}
                </TableCell>
                <TableCell onClick={() => handleSort(1)}>
                  <Stack direction="row" justifyContent="space-between">
                    <b>Business Name</b> {renderSortArrow(1)}
                  </Stack>
                </TableCell>
                <TableCell onClick={() => handleSort(2)}>
                  <Stack direction="row" justifyContent="space-between">
                    <b>Email</b> {renderSortArrow(2)}
                  </Stack>
                </TableCell>
                <TableCell onClick={() => handleSort(3)}>
                  <Stack direction="row" justifyContent="space-between">
                    <b>Location</b> {renderSortArrow(3)}
                  </Stack>
                </TableCell>
                <TableCell onClick={() => handleSort(4)}>
                  <Stack direction="row" justifyContent="space-between">
                    <b>Status</b> {renderSortArrow(4)}
                  </Stack>
                </TableCell>
                <TableCell onClick={() => handleSort(5)}>
                  <Stack direction="row" justifyContent="space-between">
                    <b>Logins</b> {renderSortArrow(5)}
                  </Stack>
                </TableCell>
                <TableCell onClick={() => handleSort(6)}>
                  <Stack direction="row" justifyContent="space-between">
                    <b>Last Login</b> {renderSortArrow(6)}
                  </Stack>
                </TableCell>
                <TableCell onClick={() => handleSort(7)}>
                  <Stack direction="row" justifyContent="space-between">
                    <b>URL</b> {renderSortArrow(7)}
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isSearchingCompaniesData ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        alignSelf: 'center',
                        width: 'auto',
                        height: '150px',
                        margin: 'auto',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : searchCompaniesData?.object?.employers?.length > 0 ? (
                <>
                  {searchCompaniesData.object.employers.map(
                    (row: {
                      email: String;
                      num_logins: String;
                      last_login: Date;
                      company: Company;
                    }) => (
                      <ManageCompanyData row={row} />
                    ),
                  )}
                </>
              ) : searchCompaniesData?.object?.employers?.length < 1 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        alignSelf: 'center',
                        width: 'auto',
                        height: '300px',
                        margin: 'auto',
                      }}
                    >
                      No Data Found
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        alignSelf: 'center',
                        width: 'auto',
                        height: '300px',
                        margin: 'auto',
                      }}
                    >
                      {applicationsListAlertText}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={searchCompaniesData?.object?.total_employes || 0}
          rowsPerPage={rowsPerPage}
          page={page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Stack>
      {showAddComapaniesModal && (
        <AddCompanyEnterpriseModal
          showAddComapaniesModal={showAddComapaniesModal}
          handleClose={() => setShowAddComapaniesModal(false)}
        />
      )}
      {showPostJobModal && (
        <AdminPostHiringLinkModal
          isOpen={showPostJobModal}
          handleClose={() => setIsPostLinkModalOpen(false)}
          item={authData?.user as unknown as Employer}
        />
      )}
      <NeedCustomHiringGraphicModal
        isOpen={isGraphicModalOpen}
        handleClose={() => setIsGraphicModalOpen(false)}
        handleRequestGraphicSuccess={() => {
          setIsGraphicModalOpen(false);
          setIsGraphicRequestedModalOpen(true);
        }}
        handleUploadImage={() => {
          setIsGraphicModalOpen(false);
          setIsSelectImageModalOpen(true);
        }}
        handleContinueWithout={() => {
          setIsGraphicModalOpen(false);
          setIsPostLinkModalOpen(true);
        }}
      />
    </div>
  );
}

export default EnterPriseCompanies;
